import { Loader2, UserX } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { BlockUserConfirmDialog } from "@/components/ui/BlockUserConfirmDialog";
import { Button } from "@/components/ui/button";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import { useBlockedUsersDetails } from "@/hooks/useBlockedUsersDetails";
import { useUserBlockActions } from "@/hooks/useUserBlockActions";

interface BlockedUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BlockedUsersModal: React.FC<BlockedUsersModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const [unblockDialogState, setUnblockDialogState] = useState<{
    isOpen: boolean;
    user: { id: number; name: string; avatarUrl: string | null } | null;
  }>({ isOpen: false, user: null });

  const { blockedUsers, isLoading, isError, refresh } =
    useBlockedUsersDetails(isOpen);
  const { unblockUserWithFeedback, isPending } = useUserBlockActions({
    onBlockedChange: refresh,
  });

  const handleRefresh = () => {
    void refresh();
  };

  const handleConfirmUnblock = async () => {
    if (!unblockDialogState.user) return;

    try {
      await unblockUserWithFeedback(unblockDialogState.user.id);
      setUnblockDialogState({ isOpen: false, user: null });
    } catch (error) {
      console.error("Error unblocking user:", error);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      icon={UserX}
      maxWidth="max-w-3xl"
      title={t("common.blockedUsers")}
      footer={
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t("common.blockedUsersDescription")}
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
            <Loader2 className="size-5 animate-spin" />
            <span>{t("common.loading")}</span>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm text-red-700">
              {t("common.blockedUsersError")}
            </p>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              {t("common.retry")}
            </Button>
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
            {t("common.blockedUsersEmpty")}
          </div>
        ) : (
          <ul className="space-y-3">
            {blockedUsers.map((blockedUser) => (
              <li
                key={blockedUser.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 p-4"
              >
                {blockedUser.displayName ? (
                  <CustomAvatar
                    name={blockedUser.displayName}
                    imageUrl={blockedUser.imageUrl ?? undefined}
                    size={40}
                    roundedFull
                  />
                ) : (
                  <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                    <UserX className="size-5" />
                  </div>
                )}

                <div className="flex flex-1 flex-col">
                  <span className="font-medium text-gray-900">
                    {blockedUser.isLoading
                      ? t("common.loading")
                      : (blockedUser.displayName ?? t("common.user"))}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUnblockDialogState({
                      isOpen: true,
                      user: {
                        id: Number(blockedUser.id),
                        name: blockedUser.displayName || t("common.user"),
                        avatarUrl: blockedUser.imageUrl,
                      },
                    })
                  }
                  disabled={blockedUser.isLoading || blockedUser.isError}
                >
                  {t("common.unblock")}
                </Button>

                {blockedUser.isLoading && (
                  <Loader2 className="size-4 animate-spin text-gray-400" />
                )}
                {blockedUser.isError && !blockedUser.isLoading && (
                  <span className="text-xs text-red-500">
                    {t("common.blockedUsersPartialError")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {unblockDialogState.user && (
        <BlockUserConfirmDialog
          isOpen={unblockDialogState.isOpen}
          onClose={() => setUnblockDialogState({ isOpen: false, user: null })}
          dialogTitle={t("common.confirmUnblockTitle", "Unblock User")}
          dialogDescription={t(
            "common.confirmUnblockDescription",
            "Are you sure you want to unblock this user?",
          )}
          cancelText={t("common.cancel")}
          confirmText={t("common.unblock")}
          isLoading={isPending}
          onConfirm={handleConfirmUnblock}
          variant="secondary"
        />
      )}
    </SettingsModalLayout>
  );
};
